import { Badge, EllipsisVerticalIcon } from '@fukuri-admins/ui/common';
import { IconButton, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import { Notification } from '../../../types/resources/Notification';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import { ActionTableSortParamsType } from '../../../types/table/ActionTableSortParamsType';
import { convertDateTime } from '../../../utils/dateTime';
import { TableRoot } from '../../common/Table/TableBase/TableRoot';
import styles from './styles.module.scss';

interface PushNotificationTableContentProps {
  columns: ActionTableColumnsType[];
  rows: Notification[];
  sortParams: ActionTableSortParamsType;
  headerSort: (column: string) => void;
  onClickEdit?: (notificationId: number) => void;
  onClickDelete?: (notificationId: number) => void;
}

export const PushNotificationTableContent: React.FC<PushNotificationTableContentProps> = ({
  columns,
  rows,
  sortParams,
  headerSort,
  onClickEdit,
  onClickDelete,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedNotificationId, setSelectedNotificationId] = useState<number | null>(null);

  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>, notificationId: number) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedNotificationId(notificationId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedNotificationId(null);
  };

  const handleClickEdit = () => {
    if (selectedNotificationId && onClickEdit) {
      onClickEdit(selectedNotificationId);
    }
    handleCloseMenu();
  };

  const handleClickDelete = () => {
    if (selectedNotificationId && onClickDelete) {
      onClickDelete(selectedNotificationId);
    }
    handleCloseMenu();
  };

  const getNestedValue = (obj: Notification, path: string) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const getStatusBadge = (deliverAt: number) => {
    if (!deliverAt) {
      return <Badge status='neutral' variant='subtle' size='sm' label='-' />;
    }
    const now = new Date();
    const deliverDate = new Date(deliverAt * 1000);
    const isDelivered = deliverDate <= now;
    return (
      <Badge
        status={isDelivered ? 'success' : 'neutral'}
        variant='subtle'
        size='sm'
        label={isDelivered ? '配信完了' : '配信予約'}
      />
    );
  };

  const formatTableCellValue = (row: Notification, columnKey: string) => {
    if (columnKey === 'status') {
      return getStatusBadge(row.deliver_at);
    }

    if (columnKey === 'group') {
      return row.group?.name ?? '-';
    }

    const value = getNestedValue(row, columnKey);

    if (columnKey === 'target_user_count' && value === 0) {
      return '-';
    }

    if (columnKey === 'deliver_at') {
      return convertDateTime(value);
    }

    if (columnKey === 'body' && typeof value === 'string' && value.length > 10) {
      return `${value.slice(0, 10)}...`;
    }

    if (typeof value === 'string' && value.length > 25) {
      return `${value.slice(0, 25)}...`;
    }

    return value ?? '-';
  };

  const tableRows = rows.map((row: Notification, key: number) => (
    <tr key={key} className={styles.tableRow} data-testid={`table-row-${key}`}>
      {columns.map((column, columnIndex) => (
        <td
          key={column.key}
          className={
            column.key === 'action' || column.key === 'status'
              ? styles.tableCellAction
              : styles.tableCell
          }
          data-testid={`table-cell-${columnIndex === 0 ? 'no' : column.key}`}
        >
          {column.key === 'action' ? (
            <IconButton
              onClick={e => handleClickMenu(e, row.id)}
              className={styles.iconButton}
              size='small'
            >
              <EllipsisVerticalIcon />
            </IconButton>
          ) : (
            formatTableCellValue(row, column.key)
          )}
        </td>
      ))}
    </tr>
  ));

  if (rows.length === 0 || rows === null) {
    return <div className={styles.emptyTable}>データなし</div>;
  }

  return (
    <>
      <TableRoot
        columns={columns}
        rows={tableRows}
        sortParams={sortParams}
        onClickHeader={headerSort}
      />
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleClickEdit} className={styles.menuItem}>
          編集
        </MenuItem>
        <MenuItem onClick={handleClickDelete} className={styles.menuItemDelete}>
          削除
        </MenuItem>
      </Menu>
    </>
  );
};
